<template>
  <div class="realtime-quote">
    <!-- 连接状态指示器 -->
    <div class="connection-status" :class="connectionStatusClass">
      <i :class="connectionStatusIcon"></i>
      <span>{{ connectionStatusText }}</span>
    </div>

    <!-- 搜索栏 -->
    <div class="search-card">
      <el-row :gutter="16">
        <el-col :span="10">
          <el-select
            v-model="selectedCode"
            filterable
            placeholder="请选择股票"
            @change="handleStockChange"
            style="width: 100%"
          >
            <el-option
              v-for="stock in stockList"
              :key="stock.stock_code"
              :label="`${stock.stock_name} (${stock.stock_code})`"
              :value="stock.stock_code"
            />
          </el-select>
        </el-col>
        <el-col :span="8">
          <el-input
            v-model="inputCode"
            placeholder="或输入股票代码"
            @keyup.enter.native="handleInputCode"
          >
            <el-button
              slot="append"
              icon="el-icon-search"
              @click="handleInputCode"
            >
              查询
            </el-button>
          </el-input>
        </el-col>
        <el-col :span="6">
          <el-button
            type="primary"
            :icon="isConnected ? 'el-icon-video-pause' : 'el-icon-video-play'"
            @click="toggleConnection"
            style="width: 100%"
          >
            {{ isConnected ? '已连接' : '连接' }}
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 行情数据显示 -->
    <div v-if="quoteData" class="quote-container">
      <!-- 主要行情信息 -->
      <el-row :gutter="20" class="main-info">
        <el-col :span="6">
          <div class="info-card stock-name-card">
            <div class="stock-name">{{ quoteData.name || '-' }}</div>
            <div class="stock-code">{{ quoteData.stock_code || '-' }}</div>
            <div class="update-time">更新时间: {{ quoteData.timestamp || '-' }}</div>
          </div>
        </el-col>

        <el-col :span="6">
          <div class="info-card price-card" :class="getPriceClass()">
            <div class="price-label">最新价</div>
            <div class="price-value">¥{{ formatPrice(quoteData.price) }}</div>
            <div class="price-change" :class="getChangeClass()">
              <span>{{ quoteData.change >= 0 ? '+' : '' }}{{ formatPrice(quoteData.change) }}</span>
              <span>{{ quoteData.change_pct >= 0 ? '+' : '' }}{{ quoteData.change_pct?.toFixed(2) || '-' }}%</span>
            </div>
          </div>
        </el-col>

        <el-col :span="12">
          <div class="info-card details-card">
            <el-row :gutter="12">
              <el-col :span="6">
                <div class="detail-item">
                  <span class="label">今开</span>
                  <span class="value">¥{{ formatPrice(quoteData.open) }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="detail-item">
                  <span class="label">最高</span>
                  <span class="value value-high">¥{{ formatPrice(quoteData.high) }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="detail-item">
                  <span class="label">最低</span>
                  <span class="value value-low">¥{{ formatPrice(quoteData.low) }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="detail-item">
                  <span class="label">成交量</span>
                  <span class="value">{{ formatVolume(quoteData.volume) }}</span>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-col>
      </el-row>

      <!-- 买卖盘口 -->
      <el-row :gutter="20" class="order-book">
        <el-col :span="12">
          <div class="order-card buy-card">
            <div class="order-header">
              <i class="el-icon-bottom"></i>
              <span>买盘</span>
            </div>
            <div class="order-list">
              <div
                v-for="order in reversed(quoteData.buy_orders)"
                :key="'buy-' + order.level"
                class="order-item buy-item"
              >
                <span class="order-level">买{{ order.level }}</span>
                <span class="order-price">¥{{ order.price?.toFixed(2) || '-' }}</span>
                <span class="order-volume">{{ formatVolume(order.volume) }}</span>
                <div class="order-bar">
                  <div
                    class="bar-fill buy-bar"
                    :style="{ width: getVolumePercent(order.volume, 'buy') + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </el-col>

        <el-col :span="12">
          <div class="order-card sell-card">
            <div class="order-header">
              <i class="el-icon-top"></i>
              <span>卖盘</span>
            </div>
            <div class="order-list">
              <div
                v-for="order in quoteData.sell_orders"
                :key="'sell-' + order.level"
                class="order-item sell-item"
              >
                <span class="order-level">卖{{ order.level }}</span>
                <span class="order-price">¥{{ order.price?.toFixed(2) || '-' }}</span>
                <span class="order-volume">{{ formatVolume(order.volume) }}</span>
                <div class="order-bar">
                  <div
                    class="bar-fill sell-bar"
                    :style="{ width: getVolumePercent(order.volume, 'sell') + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 加载状态 -->
    <div v-else-if="connecting" class="loading-container">
      <i class="el-icon-loading" style="font-size: 48px;"></i>
      <p>正在连接...</p>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <el-empty description="请选择股票查看实时行情" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RealtimeQuoteWS',
  data() {
    return {
      stockList: [],
      selectedCode: 'sz.002959',
      inputCode: '',
      quoteData: null,
      socket: null,
      isConnected: false,
      isConnecting: false,
      reconnectTimer: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      tradingTimeCheckTimer: null,
      lastWasTradingTime: null
    }
  },
  computed: {
    connecting() {
      return this.isConnecting && !this.quoteData
    },
    isNowTradingTime() {
      return this.isTradingTime()
    },
    connectionStatusText() {
      if (this.isConnected) return '已连接'
      if (this.isConnecting) return '连接中...'
      if (!this.isNowTradingTime) return '非交易时段'
      return '未连接'
    },
    connectionStatusClass() {
      if (this.isConnected) return 'status-connected'
      if (this.isConnecting) return 'status-connecting'
      if (!this.isNowTradingTime) return 'status-off-hours'
      return 'status-disconnected'
    },
    connectionStatusIcon() {
      if (this.isConnected) return 'el-icon-circle-check'
      if (this.isConnecting) return 'el-icon-loading'
      if (!this.isNowTradingTime) return 'el-icon-time'
      return 'el-icon-circle-close'
    }
  },
  mounted() {
    this.loadStockList()
    this.restoreSelectedCode()
    this.startTradingTimeChecker()
  },
  beforeDestroy() {
    this.stopTradingTimeChecker()
    this.disconnect()
  },
  methods: {
    async loadStockList() {
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockList = response.data.data
        }
      } catch (error) {
        console.error('加载股票列表失败:', error)
      }
    },

    restoreSelectedCode() {
      const savedCode = localStorage.getItem('realtime_quote_code')
      if (savedCode) {
        this.selectedCode = savedCode
      }
    },

    handleStockChange(code) {
      localStorage.setItem('realtime_quote_code', code)
      if (this.isConnected && this.socket) {
        this.subscribe(code)
      }
    },

    handleInputCode() {
      if (this.inputCode) {
        this.selectedCode = this.inputCode
        localStorage.setItem('realtime_quote_code', this.inputCode)
        if (this.isConnected && this.socket) {
          this.subscribe(this.inputCode)
        }
      }
    },

    connect() {
      if (this.socket) {
        return
      }

      this.isConnecting = true
      let updateCount = 0
      let lastUpdateTime = Date.now()

      try {
        import('socket.io-client').then(({ io }) => {
          // 直接连接到 namespace
          const socket = io('http://localhost:7350/quote', {
            transports: ['websocket'],
            reconnection: true,
            reconnectionDelay: 1000
          })

          this.socket = socket

          socket.on('connect', () => {
            console.log('[WS] 连接成功, id:', socket.id)
            this.isConnected = true
            this.isConnecting = false
            this.reconnectAttempts = 0
            if (this.selectedCode) {
              this.subscribe(this.selectedCode)
            }
          })

          socket.on('disconnect', () => {
            console.log('[WS] 断开连接')
            this.isConnected = false
          })

          socket.on('quote_update', (data) => {
            updateCount++
            const now = Date.now()
            const diff = ((now - lastUpdateTime) / 1000).toFixed(1)
            lastUpdateTime = now
            console.log(`[WS] #${updateCount} (${diff}s) ${data.stock_code} 价格:${data.price}`)
            this.$set(this, 'quoteData', { ...data })
          })

          socket.on('connected', (data) => {
            console.log('[WS] 服务器确认:', data)
          })

          socket.on('connect_error', (err) => {
            console.error('[WS] 连接错误:', err)
            this.isConnecting = false
            this.isConnected = false
          })

        }).catch(err => {
          console.error('[WS] 加载失败:', err)
          this.isConnecting = false
        })

      } catch (error) {
        console.error('[WS] 连接失败:', error)
        this.isConnecting = false
      }
    },

    disconnect() {
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
        this.isConnected = false
      }
    },

    toggleConnection() {
      if (this.isConnected) {
        this.disconnect()
      } else {
        if (!this.isTradingTime()) {
          this.$message.warning('当前非交易时段，无法连接')
          return
        }
        this.connect()
      }
    },

    subscribe(code) {
      if (this.socket && this.isConnected) {
        const cleanCode = code.replace('sz', '').replace('sh', '').replace('.', '')
        this.socket.emit('subscribe', { code: cleanCode })
        console.log('订阅股票:', cleanCode)
      }
    },

    formatPrice(price) {
      if (price === null || price === undefined) return '-'
      return Number(price).toFixed(2)
    },

    formatVolume(volume) {
      if (volume === null || volume === undefined) return '-'
      const v = Number(volume)
      if (v >= 100000000) {
        return (v / 100000000).toFixed(2) + '亿'
      } else if (v >= 10000) {
        return (v / 10000).toFixed(2) + '万'
      }
      return v.toLocaleString()
    },

    getPriceClass() {
      if (!this.quoteData || !this.quoteData.change) return ''
      return this.quoteData.change > 0 ? 'price-up' : this.quoteData.change < 0 ? 'price-down' : ''
    },

    getChangeClass() {
      if (!this.quoteData || !this.quoteData.change) return ''
      return this.quoteData.change > 0 ? 'value-up' : this.quoteData.change < 0 ? 'value-down' : ''
    },

    getVolumePercent(volume, type) {
      if (!this.quoteData) return 0

      const orders = type === 'buy' ? this.quoteData.buy_orders : this.quoteData.sell_orders
      if (!orders || orders.length === 0) return 0

      const maxVolume = Math.max(...orders.map(o => o.volume || 0))
      if (maxVolume === 0) return 0

      return ((volume || 0) / maxVolume) * 100
    },

    reversed(arr) {
      if (!arr) return []
      return [...arr].reverse()
    },

    // 判断当前是否是交易时间
    isTradingTime() {
      const now = new Date()
      const day = now.getDay()
      const hour = now.getHours()
      const minute = now.getMinutes()

      // 周末非交易日
      if (day === 0 || day === 6) {
        return false
      }

      // 交易时段: 9:30-11:30, 13:00-15:00
      const time = hour * 60 + minute
      const morningStart = 9 * 60 + 30  // 9:30
      const morningEnd = 11 * 60 + 30   // 11:30
      const afternoonStart = 13 * 60    // 13:00
      const afternoonEnd = 15 * 60      // 15:00

      return (time >= morningStart && time <= morningEnd) ||
             (time >= afternoonStart && time <= afternoonEnd)
    },

    // 启动交易时间检查定时器
    startTradingTimeChecker() {
      // 初始化状态
      this.lastWasTradingTime = this.isTradingTime()

      // 如果当前是交易时间，立即连接
      if (this.lastWasTradingTime) {
        this.connect()
      } else {
        console.log('[WS] 当前非交易时段，等待交易时段开始...')
      }

      // 每分钟检查一次交易时间
      this.tradingTimeCheckTimer = setInterval(() => {
        const nowTradingTime = this.isTradingTime()

        // 状态变化时处理
        if (nowTradingTime !== this.lastWasTradingTime) {
          this.lastWasTradingTime = nowTradingTime

          if (nowTradingTime) {
            console.log('[WS] 进入交易时段，开始连接...')
            this.connect()
          } else {
            console.log('[WS] 离开交易时段，断开连接...')
            this.disconnect()
          }
        }
      }, 60000) // 每分钟检查一次
    },

    // 停止交易时间检查定时器
    stopTradingTimeChecker() {
      if (this.tradingTimeCheckTimer) {
        clearInterval(this.tradingTimeCheckTimer)
        this.tradingTimeCheckTimer = null
      }
    }
  }
}
</script>

<style scoped>
.realtime-quote {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 连接状态 */
.connection-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
}

.status-connected {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.status-connecting {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.status-disconnected {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-off-hours {
  background: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.3);
}

/* 搜索卡片 */
.search-card {
  padding: 20px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  margin-bottom: 20px;
}

/* 行情容器 */
.quote-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 主信息行 */
.main-info {
  margin-bottom: 0;
}

/* 信息卡片 */
.info-card {
  padding: 24px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* 股票名称卡片 */
.stock-name-card {
  text-align: center;
}

.stock-name {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
}

.stock-code {
  font-size: 16px;
  color: #94a3b8;
  margin-bottom: 12px;
}

.update-time {
  font-size: 12px;
  color: #64748b;
}

/* 价格卡片 */
.price-card {
  text-align: center;
  transition: all 0.3s ease;
}

.price-card.price-up {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
  border-color: rgba(34, 197, 94, 0.3);
}

.price-card.price-down {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%);
  border-color: rgba(239, 68, 68, 0.3);
}

.price-label {
  font-size: 14px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.price-value {
  font-size: 42px;
  font-weight: 700;
  color: #e2e8f0;
  margin-bottom: 12px;
  font-family: 'DIN Alternate', monospace;
}

.price-change {
  display: flex;
  justify-content: center;
  gap: 16px;
  font-size: 16px;
  font-weight: 600;
}

.value-up {
  color: #22c55e;
}

.value-down {
  color: #ef4444;
}

/* 详情卡片 */
.details-card {
  padding: 20px 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.detail-item .label {
  font-size: 12px;
  color: #94a3b8;
}

.detail-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #e2e8f0;
}

.value-high {
  color: #ef4444;
}

.value-low {
  color: #22c55e;
}

/* 订单卡片 */
.order-book {
  margin-bottom: 0;
}

.order-card {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  overflow: hidden;
}

.order-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  background: rgba(15, 23, 42, 0.5);
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  font-weight: 600;
  color: #a5b4fc;
}

.buy-card .order-header {
  color: #22c55e;
}

.sell-card .order-header {
  color: #ef4444;
}

.order-list {
  padding: 8px 0;
}

.order-item {
  display: grid;
  grid-template-columns: 60px 1fr 100px 80px;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  position: relative;
}

.order-item .order-level {
  font-size: 14px;
  color: #94a3b8;
}

.order-item .order-price {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  font-family: 'DIN Alternate', monospace;
}

.order-item .order-volume {
  font-size: 14px;
  color: #94a3b8;
}

.order-bar {
  position: relative;
  height: 4px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 2px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.buy-bar {
  background: linear-gradient(90deg, rgba(34, 197, 94, 0.6), rgba(34, 197, 94, 0.3));
}

.sell-bar {
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.6), rgba(239, 68, 68, 0.3));
}

/* 状态容器 */
.loading-container,
.empty-container {
  padding: 60px 20px;
  text-align: center;
}

.loading-container {
  color: #94a3b8;
}

.loading-container .el-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

/* 输入框和选择器样式 */
.search-card :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
}

.search-card :deep(.el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
}

.search-card :deep(.el-input__inner):focus {
  border-color: #6366f1 !important;
}

.search-card :deep(.el-select-dropdown) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
}

.search-card :deep(.el-select-dropdown__item) {
  color: #e2e8f0 !important;
}

.search-card :deep(.el-select-dropdown__item:hover) {
  background: rgba(99, 102, 241, 0.15) !important;
}

.search-card :deep(.el-select-dropdown__item.selected) {
  color: #a5b4fc !important;
  background: rgba(99, 102, 241, 0.2) !important;
}
</style>
